import { RecipeLayer } from "@/pages/sy/models/RecipeLayer";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import type { CustomSeriesRenderItemAPI } from "echarts";
import ReactECharts from "echarts-for-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChargeLayerAudit } from "@/pages/sy/models/ChargeLayerAudit";
import { Charge } from "@/pages/sy/models/Charge";
import { useRecipeLayersQuery } from "@/pages/sy/hooks/queries/useRecipeLayersQuery";
import { useChargeLayerAuditQuery } from "@/pages/sy/hooks/queries/useChargeLayerAuditQuery";
import { ChargesColors } from "@/pages/sy/theme/colors";
import {
  getMultiCommodityFrames,
  getMultiCommodityLayerNumbers,
  groupMultiCommodityLayers,
  type LayerFrame,
  type ProcessedLayer,
} from "../utils/groupActualLayers";

interface EChartsFormatterParams {
  seriesName: string;
  name: string;
  value: number;
  dataIndex: number;
  percent?: number;
}

interface ActualRecipeChartProps {
  selectedCharge: Charge | null;
  refetchInterval: number;
}

const LABEL_LINE_HEIGHT = 18;
const SMALL_LABEL_HEIGHT = 11;
const PCT_CELL_WIDTH = 46;
const SMALL_PCT_CELL_WIDTH = 36;
const MANUAL_TAG_WIDTH = 26;
const NAME_CHAR_WIDTH = 6.5;
const SMALL_NAME_CHAR_WIDTH = 5.3;

const formatTopLine = (
  params: EChartsFormatterParams,
  layer: ProcessedLayer,
  seriesTotal: number,
  pixelsPerLb: number,
  labelWidth: number,
) => {
  const weight = params.value;
  if (weight === 0) return "";

  const manualTag = layer.isManualEdit ? " {manual|M}" : "";
  const manualWidth = layer.isManualEdit ? MANUAL_TAG_WIDTH : 0;
  const percentage = ((weight / seriesTotal) * 100).toFixed(1);
  const bandPixelHeight = weight * pixelsPerLb;

  if (bandPixelHeight < SMALL_LABEL_HEIGHT) return "";

  const nameFits =
    layer.commodityName.length * NAME_CHAR_WIDTH <=
    labelWidth - manualWidth - PCT_CELL_WIDTH;
  const smallNameFits =
    layer.commodityName.length * SMALL_NAME_CHAR_WIDTH <=
    labelWidth - manualWidth - SMALL_PCT_CELL_WIDTH;

  if (nameFits && bandPixelHeight >= LABEL_LINE_HEIGHT)
    return `{name|${layer.commodityName}}${manualTag}{pct|${percentage}%}`;
  if (smallNameFits)
    return `{smallName|${layer.commodityName}}${manualTag}{smallPct|${percentage}%}`;
  return `{small|${layer.commodityName} }{smallPctInline|${percentage}%}${manualTag}`;
};

const formatCenteredLbs = (
  params: EChartsFormatterParams,
  pixelsPerLb: number,
) => {
  const weight = params.value;
  if (weight === 0) return "";
  if (weight * pixelsPerLb < LABEL_LINE_HEIGHT * 2.5) return "";
  return `${weight.toLocaleString()} lbs`;
};

const buildSeries = (
  layers: ProcessedLayer[],
  position: "actual" | "recipe",
  seriesTotal: number,
  pixelsPerLb: number,
  bandWidth: number,
) =>
  layers.map((layer, index) => {
    const labelWidth = Math.max(0, bandWidth - 8);
    const manualWidth = layer.isManualEdit ? MANUAL_TAG_WIDTH : 0;
    return {
      id: `${position}-${layer.key ?? index}`,
      name: layer.commodityName,
      type: "bar",
      stack: "layers",
      barWidth: bandWidth,
      data: position === "actual" ? [layer.weight, 0] : [0, layer.weight],
      itemStyle: { color: layer.color },
      labelLayout: { hideOverlap: true },
      label: {
        show: true,
        position: "insideTop",
        formatter: (params: EChartsFormatterParams) =>
          formatTopLine(params, layer, seriesTotal, pixelsPerLb, labelWidth),
        fontSize: 11,
        color: ChargesColors.CHART_LABEL_TEXT,
        lineHeight: 18,
        rich: {
          manual: {
            backgroundColor: ChargesColors.CHART_MANUAL_BADGE,
            color: ChargesColors.CHART_LABEL_TEXT,
            borderRadius: 3,
            padding: [2, 6],
            fontSize: 13,
            fontWeight: "bold",
            lineHeight: 14,
            verticalAlign: "middle",
          },
          small: {
            fontSize: 9,
            color: ChargesColors.CHART_LABEL_TEXT,
            lineHeight: 10,
          },
          smallName: {
            fontSize: 9,
            color: ChargesColors.CHART_LABEL_TEXT,
            lineHeight: 10,
            align: "left",
            width: Math.max(0, labelWidth - manualWidth - SMALL_PCT_CELL_WIDTH),
          },
          smallPct: {
            fontSize: 9,
            fontWeight: "bold",
            color: ChargesColors.CHART_LABEL_TEXT,
            lineHeight: 10,
            align: "right",
            width: SMALL_PCT_CELL_WIDTH,
          },
          smallPctInline: {
            fontSize: 9,
            fontWeight: "bold",
            color: ChargesColors.CHART_LABEL_TEXT,
            lineHeight: 10,
          },
          name: {
            fontSize: 11,
            color: ChargesColors.CHART_LABEL_TEXT,
            lineHeight: 18,
            align: "left",
            width: Math.max(0, labelWidth - manualWidth - PCT_CELL_WIDTH),
          },
          pct: {
            fontSize: 11,
            fontWeight: "bold",
            color: ChargesColors.CHART_LABEL_TEXT,
            lineHeight: 18,
            align: "right",
            width: PCT_CELL_WIDTH,
          },
        },
      },
    };
  });

const buildCenteredLbsSeries = (
  layers: ProcessedLayer[],
  position: "actual" | "recipe",
  pixelsPerLb: number,
  bandWidth: number,
) =>
  layers.map((layer, index) => ({
    id: `${position}-lbs-${layer.key ?? index}`,
    name: layer.commodityName,
    type: "bar",
    stack: "layers-lbs",
    barWidth: bandWidth,
    barGap: "-100%",
    silent: true,
    z: 3,
    data: position === "actual" ? [layer.weight, 0] : [0, layer.weight],
    itemStyle: { color: "transparent" },
    label: {
      show: true,
      position: "inside",
      offset: [0, LABEL_LINE_HEIGHT / 2],
      formatter: (params: EChartsFormatterParams) =>
        formatCenteredLbs(params, pixelsPerLb),
      fontSize: 11,
      color: ChargesColors.CHART_LABEL_TEXT,
    },
  }));

const MULTI_LAYER_FRAME_WIDTH = 3;
const MULTI_LAYER_FRAME_OVERHANG = 8;

const buildMultiLayerFrameSeries = (
  frames: LayerFrame[],
  position: "actual" | "recipe",
  bandWidth: number,
) => ({
  id: `${position}-multi-layer-frames`,
  type: "custom",
  silent: true,
  z: 10,
  encode: { x: 0, y: [1, 2] },
  data: frames.map((frame) => [
    position === "actual" ? 0 : 1,
    frame.start,
    frame.end,
  ]),
  renderItem: (_params: unknown, api: CustomSeriesRenderItemAPI) => {
    const bottom = api.coord([api.value(0), api.value(1)]);
    const top = api.coord([api.value(0), api.value(2)]);
    return {
      type: "rect",
      shape: {
        x: bottom[0] - bandWidth / 2 - MULTI_LAYER_FRAME_OVERHANG,
        y: top[1],
        width: bandWidth + MULTI_LAYER_FRAME_OVERHANG * 2,
        height: bottom[1] - top[1],
      },
      style: {
        fill: "none",
        stroke: grey[900],
        lineWidth: MULTI_LAYER_FRAME_WIDTH,
      },
    };
  },
});

const buildChartOptions = (
  recipeLayers: RecipeLayer[],
  actualLayers: ChargeLayerAudit[],
  chargeName: string,
  chartHeight: number,
  chartWidth: number,
  isGroupedByLayer: boolean,
) => {
  // SP returns ORDER BY LayerNumber ASC
  const processedRecipeLayers: ProcessedLayer[] = recipeLayers.map((layer) => ({
    key: layer.RecipeLayerId,
    layerNumber: layer.LayerNumber,
    commodityName: layer.CommodityName ?? "",
    weight: layer.Weight,
    color: layer.CommodityColor || ChargesColors.CHART_COMMODITY_FALLBACK,
    isManualEdit: false,
  }));

  // Process actual layers - group by LayerGroupNumber
  // SP returns ORDER BY Timestamp ASC, Map preserves insertion order
  //
  // Manual layers (IsManualEdit = true on any audit row for a ChargeLayerId):
  //   - Collapse all groups into the first one (manual weight audit rows
  //     shouldn't create separate visual blocks)
  //   - Use CurrentWeight (absolute from ChargeLayers) instead of summing diffs
  //   - Use CurrentCommodityName/Color (operator's value takes priority)
  //
  // Non-manual layers: keep individual groups, sum WeightDifference as before
  const manualChargeLayerIds = new Set<number>();
  actualLayers.forEach((audit) => {
    if (audit.IsManualEdit) {
      manualChargeLayerIds.add(audit.ChargeLayerId);
    }
  });

  const chargeLayerFirstGroup = new Map<number, number>();
  actualLayers.forEach((audit) => {
    if (!chargeLayerFirstGroup.has(audit.ChargeLayerId)) {
      chargeLayerFirstGroup.set(audit.ChargeLayerId, audit.LayerGroupNumber);
    }
  });

  const actualLayersByGroup = new Map<number, ProcessedLayer>();
  const processedManualIds = new Set<number>();

  actualLayers.forEach((audit) => {
    const isManual = manualChargeLayerIds.has(audit.ChargeLayerId);

    if (isManual) {
      // Manual: collapse to first group, use current values from ChargeLayers
      const firstGroup = chargeLayerFirstGroup.get(audit.ChargeLayerId)!;
      if (!processedManualIds.has(audit.ChargeLayerId)) {
        processedManualIds.add(audit.ChargeLayerId);
        actualLayersByGroup.set(firstGroup, {
          chargeLayerId: audit.ChargeLayerId,
          layerNumber: audit.LayerNumber,
          recipeLayerId: audit.RecipeLayerId,
          commodityName: audit.CurrentCommodityName,
          weight: audit.CurrentWeight,
          color:
            audit.CurrentCommodityColor ||
            ChargesColors.CHART_COMMODITY_FALLBACK,
          isManualEdit: true,
        });
      }
    } else {
      // Non-manual: keep individual groups, sum weight differences
      const existing = actualLayersByGroup.get(audit.LayerGroupNumber);
      if (existing) {
        existing.weight += audit.WeightDifference;
      } else {
        actualLayersByGroup.set(audit.LayerGroupNumber, {
          chargeLayerId: audit.ChargeLayerId,
          layerNumber: audit.LayerNumber,
          recipeLayerId: audit.RecipeLayerId,
          commodityName: audit.CommodityName,
          weight: audit.WeightDifference,
          color: audit.CommodityColor || ChargesColors.CHART_COMMODITY_FALLBACK,
          isManualEdit: false,
        });
      }
    }
  });

  const sequenceLayers = Array.from(actualLayersByGroup.values()).filter(
    (layer) => layer.weight > 0,
  );
  const processedActualLayers = isGroupedByLayer
    ? groupMultiCommodityLayers(
        sequenceLayers,
        getMultiCommodityLayerNumbers(actualLayers),
      )
    : sequenceLayers;

  const recipeTotal = processedRecipeLayers.reduce(
    (sum, layer) => sum + layer.weight,
    0,
  );
  const actualTotal = processedActualLayers.reduce(
    (sum, layer) => sum + layer.weight,
    0,
  );
  const maxTotal = Math.max(actualTotal, recipeTotal);

  // Estimated plot-area height: chart minus title/grid margins, legend and
  // axis labels; 0.9 covers the y-axis auto-scale headroom above the bars
  const plotHeight = Math.max(0, chartHeight - 190);
  const pixelsPerLb = maxTotal > 0 ? (plotHeight / maxTotal) * 0.9 : 0;

  // Bars get an explicit pixel width (60% of each category band, estimated
  // from the plot area) so the in-band labels can be sized to match exactly
  const plotWidth = Math.max(0, chartWidth - 170);
  const bandWidth = Math.min(
    400,
    Math.max(40, Math.floor((plotWidth / 2) * 0.6)),
  );

  // Get all unique commodities for legend
  const allCommodities = new Set<string>();
  processedRecipeLayers.forEach((l) => allCommodities.add(l.commodityName));
  processedActualLayers.forEach((l) => allCommodities.add(l.commodityName));

  const hasManualLayers = processedActualLayers.some((l) => l.isManualEdit);

  return {
    title: {
      text: "Layer Sequence Comparison: Actual vs Recipe",
      left: "center",
      textStyle: {
        fontSize: chargeName ? 14 : 16,
        fontWeight: 600,
      },
    },
    tooltip: {
      trigger: "item",
      formatter: (params: EChartsFormatterParams) => {
        const { seriesName, value, dataIndex } = params;
        if (seriesName === "Manual Edition") return "";
        const total = dataIndex === 0 ? actualTotal : recipeTotal;
        const percentage = ((value / total) * 100).toFixed(2);
        const barType = dataIndex === 0 ? "Actual" : "Recipe";
        const isManual =
          dataIndex === 0 &&
          processedActualLayers.some(
            (l) => l.commodityName === seriesName && l.isManualEdit,
          );
        const manualLine = isManual
          ? `<br/><span style="display:inline-block;background:${ChargesColors.CHART_MANUAL_BADGE};color:${ChargesColors.CHART_LABEL_TEXT};border-radius:3px;padding:1px 6px;font-weight:bold;font-size:11px;margin-top:4px">M</span> Manual Edition`
          : "";
        return `${barType}<br/>
                Commodity: ${seriesName}<br/>
                Weight: ${value.toLocaleString()} lbs<br/>
                Percentage: ${percentage}%${manualLine}`;
      },
    },
    legend: {
      data: [
        ...Array.from(allCommodities),
        ...(hasManualLayers ? [{ name: "Manual Edition", icon: "none" }] : []),
      ],
      bottom: 10,
      itemGap: 20,
      formatter: (name: string) => {
        if (name === "Manual Edition") {
          return `{badge|M}  Manual Edition`;
        }
        return name;
      },
      textStyle: {
        rich: {
          badge: {
            backgroundColor: ChargesColors.CHART_MANUAL_BADGE,
            color: ChargesColors.CHART_LABEL_TEXT,
            borderRadius: 3,
            padding: [2, 6],
            fontSize: 11,
            fontWeight: "bold",
            verticalAlign: "middle",
          },
        },
      },
    },
    grid: {
      top: 50,
      left: 50,
      right: 50,
      bottom: 100,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: ["Actual Layers", "Recipe Layers"],
      axisLabel: {
        fontSize: 14,
        fontWeight: 600,
        formatter: (value: string, index: number) => {
          const total = index === 0 ? actualTotal : recipeTotal;
          return `${value}\n{total|${total.toLocaleString()} lbs}`;
        },
        rich: {
          total: {
            fontSize: 11,
            fontWeight: "normal",
            color: ChargesColors.CHART_AXIS_SUBTITLE,
            padding: [2, 0, 0, 0],
          },
        },
      },
    },
    yAxis: {
      type: "value",
      name: "Weight (lbs)",
      nameLocation: "middle",
      nameGap: 60,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: 600,
      },
      axisLabel: {
        formatter: (value: number) => {
          return value.toLocaleString();
        },
      },
    },
    series: [
      ...buildSeries(
        processedActualLayers,
        "actual",
        actualTotal,
        pixelsPerLb,
        bandWidth,
      ),
      ...buildSeries(
        processedRecipeLayers,
        "recipe",
        recipeTotal,
        pixelsPerLb,
        bandWidth,
      ),
      ...buildCenteredLbsSeries(
        processedActualLayers,
        "actual",
        pixelsPerLb,
        bandWidth,
      ),
      ...buildCenteredLbsSeries(
        processedRecipeLayers,
        "recipe",
        pixelsPerLb,
        bandWidth,
      ),
      buildMultiLayerFrameSeries(
        getMultiCommodityFrames(processedActualLayers),
        "actual",
        bandWidth,
      ),
      buildMultiLayerFrameSeries(
        getMultiCommodityFrames(processedRecipeLayers),
        "recipe",
        bandWidth,
      ),
      ...(hasManualLayers
        ? [
            {
              id: "manual-edition",
              name: "Manual Edition",
              type: "bar",
              stack: "layers",
              barWidth: bandWidth,
              data: [0, 0],
              itemStyle: { color: ChargesColors.CHART_MANUAL_BADGE },
              label: { show: false },
            },
          ]
        : []),
    ],
  };
};

const CenteredMessage = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
    }}
  >
    {children}
  </Box>
);

export const ActualRecipeChart = ({
  selectedCharge,
  refetchInterval,
}: ActualRecipeChartProps) => {
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const [isGroupedByLayer, setIsGroupedByLayer] = useState(false);

  useEffect(() => {
    setIsGroupedByLayer(false);
  }, [selectedCharge?.ChargeId]);

  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    if (node) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        setChartSize({
          width: entries[0].contentRect.width,
          height: entries[0].contentRect.height,
        });
      });
      resizeObserverRef.current.observe(node);
    }
  }, []);

  const { data: recipeLayers = [], isLoading: isLoadingRecipeLayers } =
    useRecipeLayersQuery(
      selectedCharge?.RecipeId,
      selectedCharge?.ChargeNumber,
    );

  const { data: actualLayers = [], isLoading: isLoadingActualLayers } =
    useChargeLayerAuditQuery(selectedCharge?.ChargeId, refetchInterval);

  if (!selectedCharge) {
    return (
      <CenteredMessage>
        <Typography>
          Select a charge from the list to view layer comparison
        </Typography>
      </CenteredMessage>
    );
  }

  if (isLoadingRecipeLayers || isLoadingActualLayers) {
    return (
      <CenteredMessage>
        <CircularProgress />
      </CenteredMessage>
    );
  }

  const chargeName = `Charge #${selectedCharge.ChargeId} - ${
    selectedCharge.BucketName || ""
  } ${selectedCharge.RecipeName || ""}`;

  const hasMultiCommodityLayers =
    getMultiCommodityLayerNumbers(actualLayers).size > 0;
  const isGroupingApplied = isGroupedByLayer && hasMultiCommodityLayers;

  return (
    <Box
      ref={containerRef}
      sx={{
        padding: 2,
        marginTop: 2,
        height: "97%",
        position: "relative",
      }}
    >
      <Tooltip
        title={
          hasMultiCommodityLayers
            ? ""
            : "This charge has no multi-commodity layers"
        }
      >
        <Box
          component="span"
          sx={{ position: "absolute", top: 0, left: 16, zIndex: 1 }}
        >
          <FormControlLabel
            disabled={!hasMultiCommodityLayers}
            control={
              <Checkbox
                size="small"
                checked={isGroupingApplied}
                onChange={(e) => setIsGroupedByLayer(e.target.checked)}
              />
            }
            label="Group by layer"
          />
        </Box>
      </Tooltip>
      <ReactECharts
        option={buildChartOptions(
          recipeLayers,
          actualLayers,
          chargeName,
          chartSize.height,
          chartSize.width,
          isGroupingApplied,
        )}
        replaceMerge={["series"]}
        style={{ width: "100%", height: "100%" }}
      />
    </Box>
  );
};
